import { LDContext, initialize } from 'launchdarkly-js-client-sdk';
import { LDProvider, LDReactOptions, useFlags } from 'launchdarkly-react-client-sdk';
import { useEqualityMemo } from 'libs/uikit/lib';
import React, { useEffect } from 'react';
import { appconfig } from '../config/appconfig';

// The launch darkley context object represents a user or some segment
// for flag targeting.
//
// We want to reduce the number of unique contexts because they are tied to billing.
//
// We likely don't need to target every user individually so we will design our
// context around something else TBD.
//
// The context can be updated by calling `client.identify({ ... })`.
//
// https://docs.launchdarkly.com/home/billing/usage-metrics#calculating-client-side-mci
const initialContext: LDContext = {
  key: '00000000-0000-0000-0000-000000000000',
  kind: 'user',
  anonymous: true,
};

const client = initialize(appconfig.launchDarkley.key, initialContext, {
  // the initial flag values will bootstrap from a previous
  // session cached in local storage.
  bootstrap: 'localStorage',
});

const reactOptions: LDReactOptions = {
  // disable camelcase flags so that flags can easily
  // be searched by their real name verbatim.
  useCamelCaseFlagKeys: false,
};

export interface FeatureContext {
  userId: number;
  email: string;
}

export interface FeatureFlagProviderProps {
  context?: FeatureContext;
  children?: React.ReactNode;
}

export function FeatureFlagProvider(props: FeatureFlagProviderProps) {
  // the equality memo is used so that we can run effects
  // based on the context object's properties changing
  // instead of the object reference itself.
  // i.e. incase the parent component initializes
  // a new object on every render with the same properties.
  const context = useEqualityMemo(props.context);

  useEffect(() => {
    if (!context) {
      return;
    }

    if (context.email.endsWith('@eventspass.com') || context.email.endsWith('@o-tix.com')) {
      client.identify({
        anonymous: false,
        kind: 'user',
        key: context.userId.toString(),
        name: context.email,
        email: context.email,
      });
    }
  }, [context]);

  return (
    <LDProvider clientSideID={appconfig.launchDarkley.key} ldClient={client} reactOptions={reactOptions}>
      {props.children}
    </LDProvider>
  );
}

/**
 * isFlagEnabled is a function to check a feature flag.
 *
 * @param name the feature flag name
 * @returns true when the flag is enabled
 */
export function isFlagEnabled(name: string): boolean {
  const flags = client.allFlags();

  return flags[name] === true;
}

/**
 * useFlag is a react hook to check a feature flag.
 *
 * the hook is subscribed to the feature flag source and will update
 * if the flag changes.
 *
 * this hook should be preferred over the `isFlagEnabled` function
 * because the latter doesn't update when the source flag changes
 * i.e. this hook is reactive but `isFlagEnabled` is not.
 *
 * @param name the feature flag name
 * @returns true when the flag is enabled
 */
export function useFlag(name: string): boolean {
  const flags = useFlags();

  return flags[name] === true;
}

// binding this method to the window to make it quick for a developer
// to check which flags are in use via devtools.
(window as any).showFlags = () => {
  // biome-ignore lint/suspicious/noConsoleLog: <explanation>
  console.log(JSON.stringify(client.allFlags(), null, 2));
};
